<script>
import { saveAs } from 'file-saver/FileSaver'
import WorkflowWidget from '@/components/common/WorkflowWidget'
import MultipleProfilePicker from '@/components/profile/MultipleProfilePicker'
import SelfServiceModeler from '@/components/self-service/SelfServiceModeler'
import InputProposal from '@/components/common/InputProposal'
import InputConstant from '@/components/common/InputConstant'
import moment from 'moment'
import { mapGetters, mapState } from 'vuex'
import axios from 'axios'

export default {
  name: 'WorkflowCard',
  props: {
    hexId: {
      type: String
    }
  },
  data() {
    return {
      isCard: false,
      workflow: null,
      editName: false,
      editDescription: false,
      editBpmn: false,
      editProcessId: false,
      editEmailList: false,
      workflowUpdating: false,
      workflowProfilesUpdating: false,
      workflowBotsUpdating: false,
      flagDeploy: false,
      showProposal: false,
      activeConfirmDialog: false,
      serviceCreation: false,
      selfServiceHexId: null,
      hasSelfService: false,
      extraButtons: [
        {
          title: 'Download Diagram as XML',
          method: () => {
            this.saveXML()
          },
          icon: 'get_app'
        },
        {
          title: 'Download Diagram as SVG',
          method: () => {
            this.saveSVG()
          },
          icon: 'image'
        }
      ],
      name: '',
      description: '',
      active: '',
      bpmnId: '',
      processId: '',
      emailList: [],
      emailRules: [
        v => (v.length > 0 ? /.+@.+/.test(v) || 'E-mail must be valid' : !!v)
      ]
    }
  },

  created() {
    if (this.hexId || this.$router.currentRoute.name === 'workflow-card') {
      if (this.$router.currentRoute.name === 'workflow-card') {
        this.isCard = true
      } else {
        this.isCard = false
      }
      const id = this.$route.params.hexId || this.hexId
      this.init(id)
    }
  },

  computed: {
    ...mapState({
      workflowProfiles: state => state.workflows.workflowProfiles,
      workflowBots: state => state.workflows.workflowBots
    }),
    ...mapGetters({
      can: 'can'
    })
  },

  watch: {
    hexId() {
      this.init(this.hexId)
    },
    workflow() {
      this.getAssociations()
      this.name = this.workflow.name
      this.description = this.workflow.description
      this.active = this.workflow.active
      this.bpmnId = this.workflow.bpmnId
      this.processId = this.workflow.processId
      this.emailList = this.workflow.supportEmailLists
        ? this.workflow.supportEmailLists.join()
        : []
      this.checkSelfService()
    }
  },

  methods: {
    gotoList() {
      this.$router.replace({ name: 'workflow-list' }).catch()
    },
    init(id) {
      this.workflowUpdating = true
      axios
        .get(`/api/v1/workflows/${id}`)
        .then(response => {
          this.workflow = response.data
          this.getAssociations()
          this.checkSelfService()
          this.workflowUpdating = false
        })
        .catch(error => {
          console.log(error)
          this.workflowUpdating = false
        })
    },
    getAssociations() {
      this.getWorkflowProfiles()
      this.getWorkflowBots()
    },
    gotoHistory() {
      this.$router.push({
        name: 'workflow-version-control',
        params: { workflow: this.workflow, hexId: this.workflow.hexId }
      })
    },

    async getWorkflowBots() {
      this.workflowBotsUpdating = true
      try {
        await this.$store.dispatch('fetchWorkflowBots', this.workflow)
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to fetch Bot(s) for Workflow',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.workflowBotsUpdating = false
    },

    async checkSelfService() {
      this.selfServiceHexId = null
      try {
        let response = await this.$store.dispatch(
          'fetchWorkflowSelfService',
          this.workflow.hexId
        )
        this.selfServiceHexId = response.data.hexId
        this.hasSelfService = true
      } catch (e) {
        this.hasSelfService = false
      }
    },

    async gotoSource() {
      this.$router.push({
        name: 'self-service-detail',
        params: { hexId: this.selfServiceHexId }
      })
    },

    async getWorkflowProfiles() {
      this.workflowProfilesUpdating = true
      try {
        await this.$store.dispatch('fetchWorkflowProfiles', this.workflow)
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to fetch Profile(s) for Workflow',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.workflowProfilesUpdating = false
    },

    async setProfiles(profileIds) {
      this.workflowProfilesUpdating = true
      try {
        await this.$store.dispatch('addProfilesToWorkflow', {
          profileIds,
          workflow: this.workflow
        })
        await this.$store.dispatch('directNotification', {
          type: 'success',
          message: 'Profile(s) updated for Workflow Successfully',
          description: 'Profile(s) updated for Workflow Successfully',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to update Profile(s)',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a'),
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry'
        })
      }
      try {
        await this.$store.dispatch('fetchWorkflowProfiles', this.workflow)
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to fetch Profile(s) for Workflow',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.workflowProfilesUpdating = false
    },

    async setActive(status) {
      this.workflowUpdating = true
      this.activeConfirmDialog = false
      if (status) {
        try {
          await this.$store.dispatch('updateWorkflow', {
            body: { active: this.active },
            hexId: this.workflow.hexId
          })
          let message = ''
          if (this.active) {
            message = 'Workflow activated Successfully'
          } else {
            message = 'Workflow de-activatated Successfully'
          }
          await this.$store.dispatch('directNotification', {
            message: message,
            type: 'success',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
        } catch (e) {
          let message = ''
          if (this.active) {
            message = 'Unable to activate Workflow'
          } else {
            message = 'Unable to de-activate Workflow'
          }
          await this.$store.dispatch('directNotification', {
            message: message,
            description: e.response
              ? e.response.data.message
              : 'Error unknown! Kindly retry',
            type: 'error',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.resetActive()
        }
      } else {
        this.resetActive()
      }
      this.workflowUpdating = false
    },

    resetActive() {
      this.active = this.workflow.active
    },

    async setConstants(value) {
      this.workflowUpdating = true
      try {
        await this.$store.dispatch('updateWorkflow', {
          body: { constants: value },
          hexId: this.workflow.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Workflow constant(s) updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Workflow constant(s) update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.workflowUpdating = false
    },

    async setProposals(value) {
      this.workflowUpdating = true
      try {
        await this.$store.dispatch('updateWorkflow', {
          body: { proposals: value },
          hexId: this.workflow.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Workflow proposal updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Workflow proposal update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      }
      this.workflowUpdating = false
    },

    async setName() {
      this.workflowUpdating = true
      this.editName = false
      try {
        await this.$store.dispatch('updateWorkflow', {
          body: { name: this.name },
          hexId: this.workflow.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Workflow renamed Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Workflow rename Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetName()
      }
      this.workflowUpdating = false
    },

    resetName() {
      this.editName = false
      this.name = this.workflow.name
    },

    async setDescription() {
      this.workflowUpdating = true
      this.editDescription = false
      try {
        await this.$store.dispatch('updateWorkflow', {
          body: { description: this.description },
          hexId: this.workflow.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Workflow description updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Workflow description update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetDescription()
      }
      this.workflowUpdating = false
    },

    async setEmailList() {
      if (this.$refs.emailForm.validate()) {
        this.workflowUpdating = true
        this.editEmailList = false
        try {
          await this.$store.dispatch('updateWorkflow', {
            body: { supportEmailLists: this.emailList.split(',') },
            hexId: this.workflow.hexId
          })
          await this.$store.dispatch('directNotification', {
            message: 'Workflow support email list updated Successfully',
            type: 'success',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
        } catch (e) {
          await this.$store.dispatch('directNotification', {
            message: 'Workflow support email list update Failed',
            description: e.response
              ? e.response.data.message
              : 'Error unknown! Kindly retry',
            type: 'error',
            timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
          })
          this.resetEmailList()
        }
        this.workflowUpdating = false
      }
    },

    resetEmailList() {
      this.editEmailList = false
      this.emailList = this.workflow.supportEmailLists.join()
    },

    resetDescription() {
      this.editDescription = false
      this.description = this.workflow.description
    },

    async setBpmn() {
      this.workflowUpdating = true
      this.editBpmn = false
      try {
        await this.$store.dispatch('updateWorkflow', {
          body: { bpmnId: this.bpmnId },
          hexId: this.workflow.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Workflow BPMN assignment updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Workflow BPMN assignment update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetBpmn()
      }
      this.workflowUpdating = false
    },

    resetBpmn() {
      this.editBpmn = false
      this.bpmnId = this.workflow.bpmnId
    },

    async setProcessId() {
      this.workflowUpdating = true
      this.editProcessId = false
      try {
        await this.$store.dispatch('updateWorkflow', {
          body: { processId: this.processId },
          hexId: this.workflow.hexId
        })
        await this.$store.dispatch('directNotification', {
          message: 'Workflow process Id updated Successfully',
          type: 'success',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          message: 'Workflow process Id  update Failed',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          type: 'error',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.resetProcessId()
      }
      this.workflowUpdating = false
    },

    resetProcessId() {
      this.editProcessId = false
      this.processId = this.workflow.processId
    },

    async deploy() {
      this.flagDeploy = true
      try {
        await this.$store.dispatch('deployWorkflow', this.workflow.hexId)

        await this.$store.dispatch('directNotification', {
          type: 'success',
          message: 'Workflow deployed Successfully',
          description:
            'The latest version of the workflow ' +
            this.workflow.hexId +
            ' has been deployed.',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.flagDeploy = false
      } catch (e) {
        await this.$store.dispatch('directNotification', {
          type: 'error',
          message: 'Unable to deploy Workflow',
          description: e.response
            ? e.response.data.message
            : 'Error unknown! Kindly retry',
          timestamp: moment().format('DD MMM YYYY hh:mm:ss a')
        })
        this.flagDeploy = false
      }
    },
    design() {
      this.$router.push({
        name: 'workflow-designer-detail',
        params: { workflow: this.workflow, hexId: this.workflow.hexId }
      })
    },
    saveXML() {
      this.$refs.workflowWidget
        .getBpmnCanvas()
        .saveXML({ format: true }, (err, xml) => {
          if (!err) {
            saveAs(
              new Blob([xml], { type: 'text/xml;charset=utf-8' }),
              'workflow.bpmn'
            )
          }
        })
    },
    saveSVG() {
      this.$refs.workflowWidget.getBpmnCanvas().saveSVG((err, svg) => {
        if (!err) {
          saveAs(
            new Blob([svg], { type: 'image/svg+xml;charset=utf-8' }),
            'workflow.svg'
          )
        }
      })
    }
  },

  components: {
    InputConstant,
    InputProposal,
    WorkflowWidget,
    SelfServiceModeler,
    MultipleProfilePicker
  }
}
</script>

<template>
  <v-card class="beez-card" v-if="workflow">
    <v-toolbar dense card class="elevation-2">
      <v-toolbar-title class="body-2 beez-bold "
        >{{ workflow.name }}
      </v-toolbar-title>
      <v-spacer />
      <v-tooltip bottom v-if="isCard">
        <v-btn icon flat slot="activator" @click="gotoList">
          <v-icon>list</v-icon>
        </v-btn>
        <span>Goto Workflow List</span>
      </v-tooltip>
      <v-tooltip bottom v-if="hasSelfService">
        <v-btn icon flat slot="activator" @click="gotoSource">
          <v-icon>link</v-icon>
        </v-btn>
        <span>Goto source Automation service</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          v-if="can('WORKFLOW_VERSION_CONTROL')"
          icon
          @click.stop="gotoHistory"
          slot="activator"
        >
          <v-icon>history</v-icon>
        </v-btn>
        <span class="ml-2"> Workflow Process Version management </span>
      </v-tooltip>
      <v-dialog
        max-width="60%"
        v-model="serviceCreation"
        v-if="can('WORKFLOW_SERVICE')"
      >
        <v-tooltip bottom slot="activator" v-if="!hasSelfService">
          <v-btn icon @click.stop="serviceCreation = true" slot="activator">
            <v-icon>add</v-icon>
          </v-btn>
          <span class="ml-2"> Register as an Automation Service </span>
        </v-tooltip>
        <template>
          <v-card>
            <self-service-modeler
              :selected-item="workflow"
              is-workflow
              @cancel="serviceCreation = false"
            />
          </v-card>
        </template>
      </v-dialog>
      <v-tooltip bottom>
        <v-btn
          slot="activator"
          icon
          @click="design"
          v-if="can('WORKFLOW_DESIGNER')"
        >
          <v-icon>mdi-group</v-icon>
        </v-btn>
        <span>Open in workflow designer</span>
      </v-tooltip>
      <v-tooltip bottom>
        <v-btn
          slot="activator"
          :loading="flagDeploy"
          icon
          @click="deploy"
          v-if="can('WORKFLOW_DEPLOY')"
        >
          <v-icon>mdi-truck-fast</v-icon>
        </v-btn>
        <span>Deploy workflow</span>
      </v-tooltip>
      <v-dialog
        v-model="activeConfirmDialog"
        persistent
        max-width="290"
        :class="{ 'pull-right': $vuetify.breakpoint.mdAndUp }"
        v-if="can('WORKFLOW_STATUS')"
      >
        <input
          class="apple-switch"
          type="checkbox"
          label="Active"
          color="success"
          v-model="active"
          slot="activator"
        />

        <v-card style="border-radius: 5px;">
          <div v-if="!active">
            <v-card-text class="subheading"
              >Confirm de-activating the Workflow - {{ workflow.name }}
            </v-card-text>
          </div>
          <div v-else>
            <v-card-text class="subheading"
              >Confirm activating the Workflow - {{ workflow.name }}
            </v-card-text>
          </div>
          <v-card-actions>
            <v-spacer />
            <v-btn flat icon @click="setActive(false)">
              <v-icon>close</v-icon>
            </v-btn>
            <v-btn flat icon class="primary" @click="setActive(true)">
              <v-icon>check</v-icon>
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>
    </v-toolbar>
    <v-progress-linear
      indeterminate
      class="ma-0"
      color="accent"
      height="5"
      :active="workflowUpdating"
    />
    <div class="pa-3">
      <v-layout align-center justify-space-between row fill-height wrap>
        <v-flex grow>
          <v-text-field
            :outline="$vuetify.dark"
            color="secondary"
            label="Name"
            v-model="name"
            :readonly="!editName"
          >
            <template slot="append">
              <template v-if="editName">
                <v-icon @click="resetName">clear</v-icon>
                <v-icon @click="setName">save</v-icon>
              </template>
              <template v-else>
                <v-icon
                  v-if="can('WORKFLOW_NAME_EDIT')"
                  @click="editName = true"
                  >edit
                </v-icon>
              </template>
            </template>
          </v-text-field>
        </v-flex>
        <v-flex grow>
          <v-text-field
            class="pl-1"
            :outline="$vuetify.dark"
            color="secondary"
            label="BPMN Identifier"
            v-model="bpmnId"
            :readonly="!editBpmn"
          >
            <template slot="append">
              <template v-if="editBpmn">
                <v-icon @click="resetBpmn">clear</v-icon>
                <v-icon @click="setBpmn">save</v-icon>
              </template>
              <template v-else>
                <v-icon
                  v-if="can('WORKFLOW_BPMN_EDIT')"
                  @click="editBpmn = true"
                  >edit
                </v-icon>
              </template>
            </template>
          </v-text-field>
        </v-flex>
      </v-layout>

      <v-textarea
        label="Description"
        v-model="description"
        :readonly="!editDescription"
        :rows="1"
        :outline="$vuetify.dark"
        no-resize
        color="secondary"
      >
        <template slot="append">
          <template v-if="editDescription">
            <v-icon @click="resetDescription">clear</v-icon>
            <v-icon @click="setDescription">save</v-icon>
          </template>
          <template v-else>
            <v-icon
              v-if="can('WORKFLOW_DES_EDIT')"
              @click="editDescription = true"
              >edit
            </v-icon>
          </template>
        </template>
      </v-textarea>

      <v-text-field
        :outline="$vuetify.dark"
        label="Workflow Trigger - Process Id"
        v-model="processId"
        color="secondary"
        :readonly="!editProcessId"
      >
        <template slot="append">
          <template v-if="editProcessId">
            <v-icon @click="resetProcessId">clear</v-icon>
            <v-icon @click="setProcessId">save</v-icon>
          </template>
          <template v-else>
            <v-icon
              v-if="can('WORKFLOW_PROCESSID_EDIT')"
              @click="editProcessId = true"
              >edit
            </v-icon>
          </template>
        </template>
      </v-text-field>

      <v-flex grow>
        <v-form ref="emailForm">
          <v-text-field
            :outline="$vuetify.dark"
            color="secondary"
            label="Support Email List"
            v-model="emailList"
            :rules="emailRules"
            :readonly="!editEmailList"
          >
            <template slot="append">
              <template v-if="editEmailList">
                <v-icon @click="resetEmailList">clear</v-icon>
                <v-icon @click="setEmailList">save</v-icon>
              </template>
              <template v-else>
                <v-icon
                  v-if="can('WORKFLOW_EMAIL_EDIT')"
                  @click="editEmailList = true"
                  >edit
                </v-icon>
              </template>
            </template>
          </v-text-field>
        </v-form>
      </v-flex>

      <div class="py-4" v-if="can('WORKFLOW_INPUT_PROPOSAL')">
        <input-proposal
          :key="workflow.hexId + 'input-render'"
          :input-proposal="workflow.proposals"
          @save="setProposals"
        />
      </div>
      <div class="pb-4" v-if="can('WORKFLOW_CONSTANTS')">
        <input-constant
          :key="workflow.hexId + 'constant-render'"
          :input-constant="workflow.constants"
          @save="setConstants"
        />
      </div>
      <div
        class="pb-4"
        v-if="
          Array.isArray(workflowBots[workflow.hexId]) &&
            workflowBots[workflow.hexId].length &&
            can('WORKFLOW_BOTS')
        "
      >
        <v-card class="fill-height">
          <v-layout column fill-height>
            <v-flex shrink>
              <v-toolbar dense card class="elevation-2">
                <v-toolbar-title class="body-2 beez-bold "
                  >Bots
                </v-toolbar-title>
                <v-spacer></v-spacer>
              </v-toolbar>
              <v-progress-linear
                indeterminate
                class="ma-0"
                color="accent"
                height="5"
                :active="workflowBotsUpdating"
              />
            </v-flex>
            <v-flex grow>
              <transition name="bounce" mode="out-in">
                <v-list
                  two-line
                  key="view-bot-list"
                  style="max-height: 760px"
                  class="scroll-y"
                >
                  <template v-for="(bot, i) in workflowBots[workflow.hexId]">
                    <v-hover :key="i + '-hover-key'">
                      <v-list-tile-content slot-scope="{ hover }">
                        <v-container fulid>
                          <v-layout row>
                            <v-flex shrink pl-3>
                              <v-avatar size="40">
                                <img src="/images/Bot.svg" alt="link" />
                              </v-avatar>
                            </v-flex>
                            <v-flex grow pl-3>
                              <v-list-tile-title
                                >{{ bot.name }}
                              </v-list-tile-title>
                              <v-list-tile-sub-title
                                >{{ bot.description }}
                              </v-list-tile-sub-title>
                            </v-flex>
                            <v-flex shrink>
                              <v-tooltip bottom>
                                <v-btn
                                  v-if="hover"
                                  slot="activator"
                                  icon
                                  flat
                                  small
                                  :to="{
                                    name: 'bot-detail',
                                    params: { hexId: bot.hexId }
                                  }"
                                >
                                  <v-icon>link</v-icon>
                                </v-btn>
                                <span>Quick Link</span>
                              </v-tooltip>
                            </v-flex>
                          </v-layout>
                        </v-container>
                      </v-list-tile-content>
                    </v-hover>
                  </template>
                </v-list>
              </transition>
            </v-flex>
          </v-layout>
        </v-card>
      </div>
      <multiple-profile-picker
        @finish="setProfiles"
        :outer-updating="workflowProfilesUpdating"
        :value="workflowProfiles[workflow.hexId]"
      >
      </multiple-profile-picker>
      <div
        class="pt-4"
        :class="{ 'workflow-widget': true }"
        v-if="can('WORKFLOW_DESIGN')"
      >
        <v-card class="fill-height">
          <v-toolbar dense card class="elevation-2">
            <v-toolbar-title class="body-2 beez-bold "
              >Workflow Design
            </v-toolbar-title>
            <v-spacer />
          </v-toolbar>
          <v-layout column fill-height>
            <v-flex grow>
              <transition name="bounce" mode="out-in">
                <v-card-text
                  :key="workflow.hexId"
                  class="fill-height workflow-widget__background"
                  style="height: 200px"
                >
                  <workflow-widget
                    :bpmn-xml="workflow.data"
                    zoom-reset-button
                    zoom-in-button
                    zoom-out-button
                    :extra-buttons="extraButtons"
                    ref="workflowWidget"
                  >
                  </workflow-widget>
                </v-card-text>
              </transition>
            </v-flex>
          </v-layout>
        </v-card>
      </div>
    </div>
  </v-card>
</template>

<style scoped>
.workflow-widget {
  height: 300px;
}

.workflow-widget__background {
  background: #fafafa;
}
</style>
